import React, { useContext, useState } from 'react';

import { FormControl, FormGroup, InputLabel, MenuItem, Select } from '@material-ui/core';
import tw, { styled } from 'twin.macro';

import { FadeInComponent } from '@/components/FadeInComponent';
import { AuthContext } from '@/core/auth/AuthContext';
import { CustomReplyToField } from '@/core/users/UserInvite/CustomReplyToField';
import { UserInviteContext } from '@/core/users/UserInvite/UserInviteContext';
import { GRID_CLASSES, GRID_COLUMNS, ICON_COLUMN_SIZE } from '@/core/users/UserPermissions/UserPermissionsContentWidth';
import { ReactComponent as AddressReplyIcon } from '@/icons/address_reply.svg';

export function UserInviteReplyToAddress () {
  const { currentUser, currentSubdomain } = useContext(AuthContext);
  const { inviteParams, setInviteParams, errors, submitCount } = useContext(UserInviteContext);
  const [replyToSelection, setReplyToSelection] = useState(inviteParams.replyTo);

  const subdomainSpecificReplyAddress = `no-reply+${currentSubdomain!.key}@feedback-poweredby.uservoice.com`;

  const handleReplyToUpdate = (value: string) => {
    setReplyToSelection(value);

    inviteParams.replyTo = value === 'custom' ? '' : value;
    setInviteParams(inviteParams);
  };

  return (
    <FadeInComponent
      layout="position"
      className={`py-16 ${GRID_CLASSES}`}
      style={{ gridTemplateColumns: GRID_COLUMNS }}
    >
      <div className="col-start-2" style={{ height: ICON_COLUMN_SIZE }}>
        <div className="flex items-center justify-center h-full text-purple-600 bg-purple-200">
          <AddressReplyIcon className="w-5 h-5" />
        </div>
      </div>
      <div className="col-start-3 col-span-full">
        <h2 className="text-xl font-medium text-gray-900">Reply-to address</h2>
        <p className="mt-3 font-light">For deliverability purposes, the email will be sent from {subdomainSpecificReplyAddress} with a separate “reply-to” address, allowing users to reply to the address of your choice.</p>
      </div>
      <FormGroup className="flex-row col-start-3 col-span-full">
        <FormControl variant="outlined" className="w-80 h-14">
          <InputLabel htmlFor="reply-to-select">Reply-to address</InputLabel>
          <Select
            autoWidth
            value={replyToSelection}
            onChange={(e) => handleReplyToUpdate(e.target.value as string)}
            label="Reply-to address"
            inputProps={{ id: 'reply-to-select' }}
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              getContentAnchorEl: null,
            }}
          >
            <StyledMenuItem value={currentUser!.email}>
              {currentUser!.email}
            </StyledMenuItem>
            <StyledMenuItem className="border-0" value={subdomainSpecificReplyAddress}>
              {subdomainSpecificReplyAddress}
            </StyledMenuItem>
            <StyledMenuItem value="custom">
              Custom email
            </StyledMenuItem>
          </Select>
        </FormControl>
        {replyToSelection === 'custom' && (
          <CustomReplyToField
            email={inviteParams.replyTo}
            errorMessage={submitCount > 0 ? errors.replyTo : undefined}
            onChange={e => {
              inviteParams.replyTo = e.target.value;
              setInviteParams(inviteParams);
            }}
          />
        )}
      </FormGroup>
    </FadeInComponent>
  );
}

const StyledMenuItem = styled(MenuItem)`
  background-color: white;
  &.Mui-selected {
    background-color: white;
  }
  &.Mui-focusVisible {
    ${tw`bg-blue-100`}
  }
`;
